import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './styles/inProgress.module.css';
import sharedStyles from './styles/gameCard.module.css';
import { useGameContext } from '../../context/GameContext';
import { useLeagueSettings } from '../../context/LeagueSettingsContext';
import HiddenText from '../HiddenText';

// Define the SportsFootballIcon component here
const SportsFootballIcon = () => (
  <svg className={styles.possessionIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24px" height="24px">
    <path d="M3.02 15.62c-.08 2.42.32 4.34.67 4.69s2.28.76 4.69.67l-5.36-5.36zM13.08 3.28c-2.33.42-4.79 1.34-6.62 3.18s-2.76 4.29-3.18 6.62l7.63 7.63c2.34-.41 4.79-1.34 6.62-3.18s2.76-4.29 3.18-6.62l-7.63-7.63zM9.9 15.5l-1.4-1.4 5.6-5.6 1.4 1.4-5.6 5.6zm11.08-7.12c.08-2.42-.32-4.34-.67-4.69s-2.28-.76-4.69-.67l5.36 5.36z"/>
  </svg>
);

const getPeriodSuffix = (periodNum) => {
  switch (periodNum) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

const formatGamePeriod = (clock, period) => {

  const periodNum = parseInt(period, 10);

  if (clock === "00:00") {
    switch (periodNum) {
      case 1: return "End of 1st";
      case 2: return "Halftime";
      case 3: return "End of 3rd";
      case 4: return "End of 4th";
      default: return "Final";
    }
  }

    const periodSuffix = getPeriodSuffix(periodNum);
  return `${clock} ${periodNum}${periodSuffix}`;
};

const getMarginColor = (margin) => {
  const absMargin = Math.abs(margin);
  if (margin > 0) {
    if (absMargin >= 14) return styles.strongWinning;
    if (absMargin >= 7) return styles.winning;
    return styles.slightlyWinning;
  }
  if (margin < 0) {
    if (absMargin >= 14) return styles.strongLosing;
    if (absMargin >= 7) return styles.losing;
    return styles.slightlyLosing;
  }
  return styles.push;
};

const formatLockLine = (lockLine, isOverUnder) => {
  if (isOverUnder) return lockLine;
  return lockLine >= 0 ? `+${lockLine}` : `${lockLine}`;
};

const getPickLabel = (pickType, game) => {
  switch (pickType) {
    case 'home': return game.home_team.abbreviation || game.home_team.name;
    case 'away': return game.away_team.abbreviation || game.away_team.name;
    case 'over': return 'Over';
    case 'under': return 'Under';
    default: return '';
  }
};

const InProgressGameCard = ({ game, displayedWeek }) => {
  const { gameUpdates = {}, locksCache, user, getLocksForWeek, getMaxPicksForWeek } = useGameContext();
  const { canSeeLocks, canSeeLockers } = useLeagueSettings();

  // Check if current user has reached max picks for this week
  const hasReachedMaxPicks = useCallback(() => {
    const locksForWeek = getLocksForWeek(displayedWeek);
    const maxPicksForWeek = getMaxPicksForWeek(displayedWeek);

    if (!locksForWeek || !Array.isArray(locksForWeek)) return false;

    const userLocksCount = locksForWeek.filter(lock =>
      lock.userId === user?.id && (lock.status === 'locked' || lock.status === 'completed' || lock.status === 'in_progress')
    ).length;

    return userLocksCount >= maxPicksForWeek;
  }, [getLocksForWeek, displayedWeek, user, getMaxPicksForWeek]);

  const hasRecentUpdate = useCallback(() => {
    if (!gameUpdates || !game?.id) return false;

    const update = gameUpdates[game.id];
    if (!update) return false;

    const isRecent = Date.now() - update.timestamp < 2000;
    return isRecent && (update.scores || update.clock);
  }, [gameUpdates, game?.id]);

  // Destructure with default values
  const {
    clock = "00:00",
    period = "1",
    situation = "",
    home_score = 0,
    away_score = 0,
    possession = ""
  } = game || {};

  const getGameLocks = useCallback(() => {
    const locksForWeek = locksCache[displayedWeek] || [];
    return locksForWeek.filter(lock => lock.gameId === game.id)
      .map(lock => {
        const margin = calculateMargin(lock.pickType, lock.lockLine, home_score, away_score);
        return {
          ...lock,
          username: lock.username || 'Unknown User',
          margin
        };
      })
      .sort((a, b) => {
        const pickOrder = { away: 0, home: 1, over: 2, under: 3 };
        return pickOrder[a.pickType] - pickOrder[b.pickType];
      });
  }, [displayedWeek, locksCache, game.id, home_score, away_score]);

  const calculateMargin = (pickType, lockLine, homeScore, awayScore) => {
    const totalScore = homeScore + awayScore;
    switch(pickType) {
      case 'home':
        return (homeScore - awayScore) + lockLine;
      case 'away':
        return (awayScore - homeScore) + lockLine;
      case 'over':
        return totalScore - lockLine;
      case 'under':
        return lockLine - totalScore;
      default:
        return 0;
    }
  };

  const gameLocks = getGameLocks();

  return (
    <div className={`${sharedStyles.gameCard} ${styles.inProgressGameCard}
      ${hasRecentUpdate() ? styles.updated : ''}`}>
      <div className={styles.gameHeader}>
        <div className={styles.gameInfo}>
          <span className={styles.dateTime}>
            {formatGamePeriod(clock, period)}
          </span>
          <span className={styles.liveIndicator}>LIVE</span>
        </div>
        {situation && (
          <div className={styles.situation}>{situation}</div>
        )}
      </div>

      <div className={styles.gridContainer}>
        {/* Away Team */}
        <div className={styles.gameRow}>
          <div className={styles.teamCol}>
            <div className={styles.teamName}>
              {game.away_team.rank &&
                <span className={`${sharedStyles.rank}`}>
                  ({game.away_team.rank})
                </span>
              }
              <span className={styles.schoolName}>{game.away_team.name}</span>
            </div>
            <span className={styles.record}>{game.away_team.record}</span>
          </div>
          <div className={styles.possessionCol}>
            {possession === 'away' && <SportsFootballIcon className={styles.possessionIcon} />}
          </div>
          <div className={styles.scoreCol}>
            {away_score}
          </div>
        </div>

        {/* Home Team */}
        <div className={styles.gameRow}>
          <div className={styles.teamCol}>
            <div className={styles.teamName}>
              {game.home_team.rank &&
                <span className={`${sharedStyles.rank}`}>
                  ({game.home_team.rank})
                </span>
              }
              <span className={styles.schoolName}>{game.home_team.name}</span>
            </div>
            <span className={styles.record}>{game.home_team.record}</span>
          </div>
          <div className={styles.possessionCol}>
            {possession === 'home' && <SportsFootballIcon className={styles.possessionIcon} />}
          </div>
          <div className={styles.scoreCol}>
            {home_score}
          </div>
        </div>
      </div>

      {/* Lock Status Bars - same structure as CompletedGameCard */}
      {gameLocks.length > 0 && (canSeeLocks() || hasReachedMaxPicks()) && (
        <div className={styles.lockStatusBars}>
          {gameLocks.map((lock) => {
            const pickLabel = getPickLabel(lock.pickType, game);
            const line = formatLockLine(lock.lockLine,
              lock.pickType === 'over' || lock.pickType === 'under'
            );

            const marginClass = getMarginColor(lock.margin);

            // Determine what to show based on league settings and user state
            const isUserLock = lock.userId === user?.id;
            const showUsername = isUserLock || canSeeLockers(displayedWeek, isUserLock, 0, 1) || hasReachedMaxPicks();
            const showPickInfo = canSeeLocks() || hasReachedMaxPicks();

            return (
              <div
                key={`${lock.userId}-${lock.pickType}`}
                className={`${styles.lockStatusBar} ${marginClass}`}
              >
                <span className={styles.userSection}>
                  {showUsername ? (
                    lock.username
                  ) : (
                    <HiddenText
                      type="locker"
                      weekNumber={displayedWeek}
                      showTooltip={true}
                    />
                  )}
                </span>
                <div className={styles.pickSection}>
                  {showPickInfo ? (
                    <span>{pickLabel} {line}</span>
                  ) : (
                    <HiddenText
                      type="lock"
                      weekNumber={displayedWeek}
                      showTooltip={true}
                    />
                  )}
                  <span className={styles.marginBadge}>
                    {lock.margin > 0 ? '+' : ''}{lock.margin.toFixed(1)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

InProgressGameCard.propTypes = {
  displayedWeek: PropTypes.number.isRequired,
  game: PropTypes.shape({
    id: PropTypes.number.isRequired,
    betting: PropTypes.shape({
      spread: PropTypes.number,
      over_under: PropTypes.number
    }).isRequired,
    start_date: PropTypes.string.isRequired,
    tv: PropTypes.string,
    home_team: PropTypes.shape({
      name: PropTypes.string.isRequired,
      rank: PropTypes.number,
      record: PropTypes.string
    }).isRequired,
    away_team: PropTypes.shape({
      name: PropTypes.string.isRequired,
      rank: PropTypes.number,
      record: PropTypes.string
    }).isRequired,
    home_score: PropTypes.number,
    away_score: PropTypes.number
  }).isRequired
};

export default InProgressGameCard;
